import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@ais-ui/ui-core';
import { ArrowUpDown } from 'lucide-react';

import { cn, formatVesselName } from '@ais-ui/utils';
import {
  useTableDensity,
  TableDensityClasses,
  TruncatedCell,
  DateTimeCell,
  NumericCell,
  ShortTextCell,
  ObjectArrayCell,
  RangeCell,
  isRangeObject,
  getRangeSortValue,
} from '../../../utils/useTableDensity';
import { myCustomFilter } from '../filterTypes';
import { LengthResult, useLengthUnits } from '../../../Units/useLengthUnits';

/**
 * Configuration for table density - can be provided externally for popup usage
 */
export interface TableDensityConfig {
  tableDensity: 'normal' | 'compact';
  densityClasses: TableDensityClasses;
}

/**
 * Renders a vessel dimension value (length, width, draught) with unit conversion.
 * Handles both simple numbers and {min, max} range objects from the backend.
 */
function DimensionCell({
  rawValue,
  convertLength,
  className,
}: {
  rawValue: unknown;
  convertLength?: (valueInMeters: number) => LengthResult;
  className?: string;
}) {
  if (isRangeObject(rawValue)) {
    const convertedMin = convertLength?.(rawValue.min);
    const convertedMax = convertLength?.(rawValue.max);

    return (
      <RangeCell
        min={convertedMin?.value ?? rawValue.min}
        max={convertedMax?.value ?? rawValue.max}
        unit={convertedMin?.abbreviation}
        className={className}
      />
    );
  }

  const numValue = Number(rawValue);
  if (!isNaN(numValue) && convertLength) {
    const converted = convertLength(numValue);
    return (
      <NumericCell className={className}>
        {converted.value.toFixed(2)} {converted.abbreviation}
      </NumericCell>
    );
  }

  const displayValue = !isNaN(numValue)
    ? numValue.toFixed(2)
    : String(rawValue ?? '');

  return <NumericCell className={className}>{displayValue}</NumericCell>;
}

/**
 * Core column creation logic - can be used with any density config
 */
export function createColumns(
  accessorKeys: string[],
  formatDateTimeWithTimeZone: (date: Date | undefined | null) => string,
  densityConfig: TableDensityConfig,
  convertLength?: (valueInMeters: number) => LengthResult,
): ColumnDef<any>[] {
  const { tableDensity, densityClasses } = densityConfig;

  return accessorKeys.map((accessorKey) => {
    switch (accessorKey.toLowerCase()) {
      case 'flag':
      case 'to flag':
      case 'from flag':
        return {
          accessorKey,
          size: 100,
          minSize: 90,
          maxSize: 110,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => (
            <div className={densityClasses.cellContent}>
              <img
                src={getValue() as string}
                alt="Flag"
                className={cn(
                  'object-contain',
                  tableDensity === 'compact' ? 'max-h-4' : 'max-h-6',
                )}
              />
            </div>
          ),
        };

      case 'distance':
      case 'length':
      case 'width':
      case 'draught':
        return {
          accessorKey,
          size: 110,
          minSize: 100,
          maxSize: 140,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ row }: { row: any }) => {
            const rawValue = row.original[accessorKey];
            return (
              <DimensionCell
                rawValue={rawValue}
                convertLength={convertLength}
                className={densityClasses.cellContent}
              />
            );
          },
          accessorFn: (row: any) => {
            const value = row[accessorKey];
            return getRangeSortValue(value);
          },
          sortingFn: (rowA: any, rowB: any, columnId: string) => {
            const a = getRangeSortValue(rowA.original[columnId]);
            const b = getRangeSortValue(rowB.original[columnId]);
            return a - b;
          },
          filterFn: myCustomFilter,
        };

      case 'begin':
      case 'end':
      case 'enter':
      case 'exit':
      case 'timestamp':
      case 'ata':
      case 'atd':
      case 'sta':
      case 'std':
      case 'date':
      case 'arrival':
      case 'departure':
      case 'dats':
      case 'act':
      case 'dea':
      case 'edats':
      case 'sdats':
      case 'end date':
      case 'start date':
      case 'cpat':
      case 'actts':
      case 'deats':
      case 'eta':
        return {
          accessorKey,
          size: 180,
          maxSize: 200,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const formattedDate = formatDateTimeWithTimeZone(
              getValue() as Date,
            );

            return (
              <DateTimeCell className={densityClasses.cellContent}>
                {formattedDate}
              </DateTimeCell>
            );
          },
        };

      case 'period':
        return {
          accessorKey,
          size: 70,
          maxSize: 90,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');
            return (
              <ShortTextCell className={densityClasses.cellContent}>
                {value}
              </ShortTextCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'imo':
      case 'mmsi':
      case 'callsign':
      case 'call sign':
        return {
          accessorKey,
          size: 110,
          minSize: 100,
          maxSize: 130,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');
            return (
              <ShortTextCell className={densityClasses.cellContent}>
                {value}
              </ShortTextCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'desc':
      case 'description':
        return {
          accessorKey,
          size: 200,
          maxSize: 300,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'name':
      case 'ship name':
        return {
          accessorKey,
          size: 150,
          maxSize: 250,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = formatVesselName(String(getValue() || ''));

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'destination':
      case 'port name':
        return {
          accessorKey,
          size: 150,
          maxSize: 250,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'route name':
      case 'departure locode':
      case 'arrival locode':
      case 'locode':
      case 'ship type':
        return {
          accessorKey,
          size: 120,
          maxSize: 180,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      case 'country':
        return {
          accessorKey,
          size: 100,
          maxSize: 150,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const value = String(getValue() || '');

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };

      default:
        return {
          accessorKey,
          size: 120,
          maxSize: 200,
          header: ({ column }: { column: any }) => (
            <Button
              variant="ghost"
              className={cn(
                'px-0 w-full flex justify-between items-center gap-1',
                densityClasses.text,
              )}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              <p className="truncate">{accessorKey}</p>
              <ArrowUpDown
                className={cn('flex-shrink-0', densityClasses.icon.small)}
              />
            </Button>
          ),
          cell: ({ getValue }: { getValue: any }) => {
            const rawValue = getValue();

            if (
              rawValue !== null &&
              rawValue !== undefined &&
              typeof rawValue === 'object' &&
              (Array.isArray(rawValue) ||
                Object.getPrototypeOf(rawValue) === Object.prototype)
            ) {
              return (
                <ObjectArrayCell
                  value={rawValue}
                  className={densityClasses.cellContent}
                />
              );
            }

            if (rawValue instanceof Date) {
              const formattedDate = formatDateTimeWithTimeZone(rawValue);
              return (
                <DateTimeCell className={densityClasses.cellContent}>
                  {formattedDate}
                </DateTimeCell>
              );
            }

            const value = String(rawValue || '');

            return (
              <TruncatedCell
                text={value}
                className={densityClasses.cellContent}
              >
                {value}
              </TruncatedCell>
            );
          },
          accessorFn: (row: any) => row[accessorKey],
          filterFn: myCustomFilter,
        };
    }
  });
}

/**
 * Hook version that uses useTableDensity from main app settings
 * This is the original hook for backwards compatibility
 */
export function useCreateColumns(
  accessorKeys: string[],
  formatDateTimeWithTimeZone: (date: Date | undefined | null) => string,
): ColumnDef<any>[] {
  const { tableDensity, densityClasses } = useTableDensity();
  const { convertLengthFixedUnit } = useLengthUnits();

  return useMemo(() => {
    return createColumns(
      accessorKeys,
      formatDateTimeWithTimeZone,
      {
        tableDensity,
        densityClasses,
      },
      convertLengthFixedUnit,
    );
  }, [
    accessorKeys,
    formatDateTimeWithTimeZone,
    tableDensity,
    densityClasses,
    convertLengthFixedUnit,
  ]);
}
